import React, { useState, useMemo, useEffect } from 'react';
import { TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowUp, ArrowDown, ChevronDown, ChevronRight } from 'lucide-react';
import TimezoneBadge from '@/components/ui/timezone-badge';
import {
  SALES_HISTORY_MONTHS,
  SalesHistoryRow,
  SalesHistorySourceRow,
  formatWholeDollars,
  formatWholeNumber,
  groupSalesHistoryByYear,
} from '@/shared/salesHistory';

// Sales History (FIG-325 / REQ-050, REQ-060): one row per year. A year with
// more than one source combines them and expands (chevron, click or Enter)
// into one indented row per source, with the Connection Id in a tooltip. A
// year with one source shows that source under the year. Amounts are whole
// dollars with the $ in each cell.

interface SalesHistoryTableProps {
  data: SalesHistoryRow[];
  loading?: boolean;
  resetSignal?: number;
  timezoneInfo?: {
    detected_timezone: string;
    timezone_offset: string;
  } | null;
  /** GMV, NMV, Payout (money) or Quantity (units). */
  metricType?: string;
}

const SourceLabel: React.FC<{ source: SalesHistorySourceRow }> = ({ source }) => (
  source.connectionId ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="cursor-help">{source.source}</span>
      </TooltipTrigger>
      <TooltipContent><p className="text-xs">Connection Id: {source.connectionId}</p></TooltipContent>
    </Tooltip>
  ) : (
    <span>{source.source}</span>
  )
);

const SalesHistoryTable: React.FC<SalesHistoryTableProps> = ({ data, loading, resetSignal = 0, timezoneInfo, metricType }) => {
  const formatAmount = metricType === 'Quantity' ? formatWholeNumber : formatWholeDollars;
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedYear('all');
    setSortDirection('desc');
    setExpandedYears(new Set());
  }, [resetSignal]);

  const years = useMemo(() => groupSalesHistoryByYear(data), [data]);
  const availableYears = useMemo(
    () => years.map((y) => y.year).sort((a, b) => parseInt(b) - parseInt(a)),
    [years],
  );

  const visibleYears = useMemo(() => {
    const filtered = selectedYear === 'all' ? years : years.filter((y) => y.year === selectedYear);
    return [...filtered].sort((a, b) => {
      const diff = (parseInt(a.year) || 0) - (parseInt(b.year) || 0);
      return sortDirection === 'desc' ? -diff : diff;
    });
  }, [years, selectedYear, sortDirection]);

  const totals = useMemo(() => {
    const months = Object.fromEntries(SALES_HISTORY_MONTHS.map((m) => [m, 0])) as Record<string, number>;
    let total = 0;
    for (const y of visibleYears) {
      for (const m of SALES_HISTORY_MONTHS) months[m] += y.months[m];
      total += y.total;
    }
    return { months, total };
  }, [visibleYears]);

  const toggleYear = (year: string) =>
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-8 text-center">
        <div className="animate-pulse">Loading sales history...</div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
        <p>No sales history data available</p>
      </div>
    );
  }

  const amountCells = (months: Record<string, number>, total: number, muted = false) => (
    <>
      {SALES_HISTORY_MONTHS.map((m) => (
        <TableCell key={m} className={`text-right tabular-nums ${muted ? 'text-muted-foreground' : 'font-medium'}`}>
          {formatAmount(months[m])}
        </TableCell>
      ))}
      <TableCell className={`text-right tabular-nums ${muted ? 'text-muted-foreground' : 'font-semibold'}`}>
        {formatAmount(total)}
      </TableCell>
    </>
  );

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Filter by Year:</span>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {availableYears.map((year) => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Data Timezone:</span>
            <TimezoneBadge
              timezone={timezoneInfo?.detected_timezone || 'UTC'}
              offset={timezoneInfo?.timezone_offset || '+00:00'}
              size="sm"
            />
          </div>
        </div>

        {/* Single scroll container so header, body, and totals share horizontal scroll and column alignment */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="relative max-h-[600px] overflow-auto">
            <table className="w-full caption-bottom text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky top-0 z-10 bg-white font-semibold border-b-2 border-gray-200 whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
                      className="h-auto p-0 font-semibold hover:bg-transparent"
                    >
                      Year
                      {sortDirection === 'desc' ? <ArrowDown className="ml-1 h-4 w-4" /> : <ArrowUp className="ml-1 h-4 w-4" />}
                    </Button>
                  </TableHead>
                  {[...SALES_HISTORY_MONTHS, 'Total'].map((column) => (
                    <TableHead
                      key={column}
                      className="sticky top-0 z-10 bg-white text-right font-semibold border-b-2 border-gray-200 whitespace-nowrap"
                    >
                      {column}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleYears.map((year) => {
                  const multiSource = year.sources.length > 1;
                  const expanded = expandedYears.has(year.year);
                  return (
                    <React.Fragment key={year.year}>
                      <TableRow
                        className={`hover:bg-gray-50 ${multiSource ? 'cursor-pointer' : ''}`}
                        onClick={multiSource ? () => toggleYear(year.year) : undefined}
                        data-testid={`sales-history-year-${year.year}`}
                      >
                        <TableCell className="font-medium whitespace-nowrap">
                          {multiSource ? (
                            // A real button carries the expanded state, so the row keeps its table semantics.
                            <button
                              type="button"
                              className="flex items-center gap-1 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                              aria-expanded={expanded}
                              aria-label={`${year.year}, ${year.sources.length} sources`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleYear(year.year);
                              }}
                            >
                              {expanded
                                ? <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                : <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
                              <span>{year.year}</span>
                            </button>
                          ) : (
                            <span>{year.year}</span>
                          )}
                          {!multiSource && (
                            <div className="pl-0 text-xs font-normal text-muted-foreground">
                              <SourceLabel source={year.sources[0]} />
                            </div>
                          )}
                        </TableCell>
                        {amountCells(year.months, year.total)}
                      </TableRow>
                      {multiSource && expanded && year.sources.map((source, i) => (
                        <TableRow key={`${year.year}-${source.connectionId ?? source.source}-${i}`} className="bg-muted/20">
                          <TableCell className="pl-8 text-xs text-muted-foreground whitespace-nowrap">
                            <span aria-hidden="true">↳ </span><SourceLabel source={source} />
                          </TableCell>
                          {amountCells(source.months, source.total, true)}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
              <TableFooter className="sticky bottom-0 z-10 border-t-2 border-gray-300 bg-gray-50 hover:bg-gray-50 [&>tr]:border-b-0">
                <TableRow className="font-bold bg-gray-100 hover:bg-gray-100">
                  <TableCell className="font-bold text-gray-900 bg-gray-100">TOTALS</TableCell>
                  {SALES_HISTORY_MONTHS.map((m) => (
                    <TableCell key={m} className="text-right font-bold text-gray-900 bg-gray-100 tabular-nums">
                      {formatAmount(totals.months[m])}
                    </TableCell>
                  ))}
                  <TableCell className="text-right font-bold text-gray-900 bg-gray-100 tabular-nums">
                    {formatAmount(totals.total)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </table>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          Showing {visibleYears.length} of {years.length} years
          {selectedYear !== 'all' && ` (filtered by ${selectedYear})`}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default SalesHistoryTable;
